/**
 * Обратная связь пользователей: оценки ответов копилота и отзывы о продукте.
 * Хранится в MongoDB (коллекция feedback); файлы — ассеты API v3 (описания в feedback_assets),
 * прежние — GridFS feedback_files.
 */
import type { Attachment, AttachmentKind, Person } from "@/modules/bugfix-agent/lib/shared/types"

export type { Attachment, AttachmentKind, Person }
export { attachmentKind, cleanMime } from "@/modules/bugfix-agent/lib/shared/types"

export type FeedbackKind = "copilot" | "product"
export type FeedbackRating = "up" | "down"
export type FeedbackStatus = "new" | "in_progress" | "resolved" | "rejected"

export const FEEDBACK_KINDS: readonly FeedbackKind[] = ["copilot", "product"]
export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = ["new", "in_progress", "resolved", "rejected"]

export const KIND_LABELS: Record<FeedbackKind, string> = { copilot: "Копилот", product: "Продукт" }
export const STATUS_LABELS: Record<FeedbackStatus, string> = { new: "Новый", in_progress: "В работе", resolved: "Решён", rejected: "Отклонён" }
export const RATING_LABELS: Record<FeedbackRating, string> = { up: "Нравится", down: "Не нравится" }

/** Быстрые причины оценки ответа копилота */
export const COPILOT_TAGS: Record<FeedbackRating, readonly string[]> = {
  up: ["Точный ответ", "Полезно", "Нашёл в базе знаний", "Быстро", "Хорошо оформлено", "Другое"],
  down: ["Неверный или неполный ответ", "Не то, о чём я спрашивал(а)", "Не нашёл в базе знаний", "Медленно или с ошибкой", "Оформление", "Другое"],
}
/** Тип отзыва о продукте */
export const PRODUCT_TAGS: readonly string[] = ["Ошибка", "Предложение", "Неудобно", "Вопрос", "Другое"]

export const MAX_FEEDBACK_ATTACHMENTS = 20
export const MAX_COMMENT = 5000
/** Сколько последних сообщений диалога (до оценённого ответа включительно) сохраняется в отзыве */
export const SNAPSHOT_MESSAGES = 10

export const FEEDBACK_ID_RE = /^fb_[a-f0-9]{16}$/
export const FEEDBACK_FILE_ID_RE = /^ff_[a-f0-9]{24}$/

/** Где был пользователь, когда оставил отзыв */
export interface FeedbackContext {
  url: string
  path: string
  title?: string
  /** desktop — браузер на компьютере, mobile — мобильный браузер или PWA, app — нативное приложение Casper */
  platform: "desktop" | "mobile" | "app"
  /** Вкладка мобильного приложения (Процедуры, Копилот…) */
  mobileTab?: string | null
  appVersion?: string | null
  userAgent?: string
  viewport?: string
  screen?: string
  pixelRatio?: number
  language?: string
  timezone?: string
  workspaceId?: number | null
  workspaceName?: string | null
  /** Где открыт копилот: страница копилота, панель процедуры, мобильная вкладка… */
  copilotScope?: string | null
  /** Режим копилота в момент оценки */
  copilotMode?: { knowledgeBase?: boolean; webSearch?: boolean } | null
  referrer?: string | null
  online?: boolean
}

/** Сообщение диалога в отзыве — то, что нужно для отрисовки в вёрстке копилота */
export interface SnapshotMessage {
  id: string
  parentId: string | null
  role: "user" | "assistant"
  content: Array<{ type: string; text?: string; image_url?: string }>
  createdAt: string
  activity?: unknown[] | null
  toolCalls?: unknown[] | null
  run?: { status?: string | null } | null
  usage?: { inputTokens?: number; outputTokens?: number; totalTokens?: number; durationSec?: number; ttftSec?: number | null } | null
}

export interface CopilotSnapshot {
  dialogId: string
  /** id оценённого ответа (сообщение на сервере) */
  messageId: string
  dialogTitle: string | null
  model: string | null
  /** Владелец диалога — для проверки, что отзыв оставлен на свой чат */
  dialogUserId: number | null
  dialogWorkspaceId: number | null
  /** Последние сообщения ветки до оценённого ответа включительно */
  messages: SnapshotMessage[]
  /** Сколько сообщений ветки выше сохранённых */
  hiddenBefore: number
  /** Сообщений в ветке до ответа включительно */
  pathLength: number
  /** Сообщений в диалоге всего (все ветки, без системных) */
  dialogMessages: number
  /** Снимок построен на клиенте — диалог не найден в базе */
  fromClient?: boolean
}

export interface FeedbackNote {
  id: string
  author: Person
  text: string
  createdAt: string
}

export interface FeedbackHistoryItem {
  at: string
  actor: Person
  action: "created" | "updated" | "status" | "note"
  text?: string
}

export interface Feedback {
  id: string
  number: number
  kind: FeedbackKind
  rating: FeedbackRating | null
  tags: string[]
  comment: string
  attachments: Attachment[]
  author: Person & { login?: string | null }
  workspaceId: number | null
  context: FeedbackContext
  copilot: CopilotSnapshot | null
  status: FeedbackStatus
  notes: FeedbackNote[]
  history: FeedbackHistoryItem[]
  /** Копии диалога, которые админы сделали для отладки (одна на админа) */
  debugCopies?: Array<{ userId: number; dialogId: string; createdAt: string }>
  createdAt: string
  updatedAt: string
}

/** Строка списка в разделе разработки */
export interface FeedbackListItem {
  id: string
  number: number
  kind: FeedbackKind
  rating: FeedbackRating | null
  tags: string[]
  comment: string
  attachments: number
  hasVideo: boolean
  author: Person
  status: FeedbackStatus
  path: string
  platform: FeedbackContext["platform"]
  dialogId: string | null
  dialogTitle: string | null
  /** Начало оценённого ответа */
  answerPreview: string | null
  createdAt: string
  updatedAt: string
}

export interface FeedbackStats {
  total: number
  byKind: Record<FeedbackKind, number>
  byStatus: Record<FeedbackStatus, number>
  byRating: Record<FeedbackRating, number>
  newCount: number
}

/** Оценка пользователя на ответ — для кнопок под сообщениями копилота */
export interface MyRating {
  feedbackId: string
  messageId: string
  rating: FeedbackRating | null
  hasDetails: boolean
}

export function snapshotText(m: Pick<SnapshotMessage, "content">): string {
  const out = m.content.filter((p) => p.type === "output_text").map((p) => p.text ?? "")
  const text = out.length ? out : m.content.filter((p) => p.type === "input_text" || p.type === "text").map((p) => p.text ?? "")
  return text.join("\n")
}
